/**
 * Getting JSON out of the browser and back into it.
 *
 * Both halves are small and both are easy to get subtly wrong — a blob URL that
 * is never revoked leaks the whole document for the life of the tab, and a file
 * read that throws on malformed JSON puts a stack trace where a sentence should
 * be. They live here so the dialogs that use them contain only their own logic.
 */

/** The file extension the exporter writes and the importer offers to filter on. */
export const SCENARIO_FILE_ACCEPT = ".json,.roi.json,application/json";

/**
 * Saves `value` as a pretty-printed JSON file.
 *
 * Two spaces of indentation because these files get read, diffed and pasted into
 * issues by people, not only by the importer.
 */
export function downloadJson(filename: string, value: unknown): void {
  const blob = new Blob([`${JSON.stringify(value, null, 2)}\n`], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  // Firefox only follows a click on an anchor that is in the document.
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next turn rather than immediately: Safari has not finished
  // reading the blob when `click()` returns.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export type FileReadResult = { ok: true; value: unknown } | { ok: false; reason: string };

/** The cap is generous — a ten-year, twelve-package scenario is a few hundred
 *  kilobytes — and exists so a mis-picked video does not lock the tab up. */
const MAX_FILE_BYTES = 4 * 1024 * 1024;

/**
 * `FileReader` rather than `Blob.text()`: the promise API is the newer of the
 * two and is missing from Safari before 14 and from jsdom, so the tests would be
 * exercising a path the browser never takes.
 */
function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("The file could not be read."));
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.readAsText(file);
  });
}

/**
 * Reads a picked file and parses it as JSON, saying why not in a sentence rather
 * than throwing. Deciding whether the JSON is a *scenario* is
 * `parseScenarioDocument`'s job, not this one's.
 */
export async function readJsonFile(file: File): Promise<FileReadResult> {
  if (file.size > MAX_FILE_BYTES) {
    return { ok: false, reason: "That file is far too large to be an ROI scenario." };
  }
  let text: string;
  try {
    text = await readText(file);
  } catch {
    return { ok: false, reason: "That file could not be read. Try choosing it again." };
  }
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false, reason: "That file is not valid JSON, so nothing could be read from it." };
  }
}
